import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { MapPin, RefreshCw } from "lucide-react";
import "leaflet/dist/leaflet.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FitBounds = ({ listeners }) => {
  const map = useMap();
  useEffect(() => {
    if (listeners.length > 0) {
      const bounds = listeners.map(l => [l.lat, l.lon]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
    }
  }, [listeners, map]);
  return null;
};

export const ListenerMap = () => {
  const [listeners, setListeners] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchListeners = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/dashboard/listeners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setListeners(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchListeners();
    intervalRef.current = setInterval(fetchListeners, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchListeners]);

  const grouped = listeners.reduce((acc, l) => {
    const key = `${l.lat},${l.lon}`;
    if (!acc[key]) acc[key] = { ...l, count: 0 };
    acc[key].count++;
    return acc;
  }, {});
  const points = Object.values(grouped);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="listener-map-card">
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-bold text-white">Listener Locations</h3>
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">
            {listeners.length} active
          </span>
        </div>
        <button onClick={fetchListeners} className="text-white/40 hover:text-white transition" data-testid="listener-map-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div style={{ height: 400 }} data-testid="listener-map-container">
        {loading && listeners.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/40">Loading map...</div>
        ) : listeners.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-white/40 gap-2">
            <MapPin className="w-8 h-8 opacity-30" />
            <p>No listeners with location data</p>
          </div>
        ) : (
          <MapContainer
            center={[30, 0]}
            zoom={2}
            scrollWheelZoom={true}
            style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <FitBounds listeners={listeners} />
            {points.map((p, i) => (
              <CircleMarker
                key={i}
                center={[p.lat, p.lon]}
                radius={Math.min(8 + p.count * 4, 24)}
                pathOptions={{
                  color: "#a855f7",
                  fillColor: "#a855f7",
                  fillOpacity: 0.6,
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ color: "#1a1a2e", fontFamily: "sans-serif", minWidth: 120 }}>
                    <p style={{ fontWeight: 700, fontSize: 14, margin: 0 }}>{p.city || "Unknown"}</p>
                    <p style={{ fontSize: 12, margin: "2px 0 0", color: "#555" }}>
                      {[p.region, p.country].filter(Boolean).join(", ")}
                    </p>
                    <p style={{ fontSize: 13, margin: "6px 0 0", fontWeight: 600, color: "#a855f7" }}>
                      {p.count} listener{p.count > 1 ? "s" : ""}
                    </p>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
};
